// btw-front/src/api/adminStaleDeals.js
// 관리 대상 딜(장기 미수정) — 조회 / 자동 알림 설정 / 즉시 발송 / 엑셀 다운로드.
import { api, getAccessToken } from "./client";

// 목록·엑셀 공통 쿼리 — days(정체 기간) + q(키워드: 파트너사·고객사·영업대표). 빈 값은 생략
function staleQuery(days, q) {
  const params = new URLSearchParams();
  if (days) params.set("days", String(days));
  if (q && q.trim()) params.set("q", q.trim());
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

// days 이상 수정 없는 진행중 딜(오래된 순). days 미지정 시 서버 설정값 사용.
// q 지정 시 파트너사·고객사·영업대표 이름 부분 일치로 거른 결과.
// 응답: { days, items: [...] }
export async function list(days, q) {
  return api.get(`/admin/stale-deals${staleQuery(days, q)}`);
}

// 자동 알림 설정 조회 — { isEnabled, thresholdDays, sendAt, lastRunOn, lastRunDealCount }
export async function getSetting() {
  return api.get(`/admin/stale-deals/setting`);
}

// 설정 수정 — { isEnabled?, thresholdDays?, sendAt? }
export async function updateSetting(patch) {
  return api.put(`/admin/stale-deals/setting`, patch);
}

// 지금(수동) 발송 — { status: 'sent'|'skipped'|'failed', dealCount, recipientCount }
export async function sendNow() {
  return api.post(`/admin/stale-deals/send-now`, {});
}

// 발송될 안내 메일 미리보기 — { recipientEmail, recipientName, staleDays, subject, text }
export async function previewNotice(dealId) {
  return api.get(`/admin/stale-deals/${encodeURIComponent(dealId)}/notice/preview`);
}

// 딜 갱신 요청 안내 메일 발송 — 등록 계정 이메일 수신. 이미 발송된 딜은 409(ALREADY_NOTICED)
export async function sendNotice(dealId) {
  return api.post(`/admin/stale-deals/${encodeURIComponent(dealId)}/notice`, {});
}

// 선택 딜 id 가드 — 체크된 딜이 없으면 요청 자체를 거부한다. 서버는 dealIds 미지정을
// "목록 전체 발송"으로 해석하므로, 빈 배열을 조용히 생략하면 전원 발송 사고로 이어짐(fail-open 금지).
// api 레이어는 i18n 미사용 — 영문 메시지, 호출자가 i18n 폴백으로 덮어쓴다.
function requireDealIds(dealIds) {
  if (!Array.isArray(dealIds) || dealIds.length === 0) {
    throw new Error("dealIds required: no deals selected");
  }
  return dealIds.map(String);
}

// 일괄 발송 미리보기 — 수신자별 그룹 + 딜 단위 후보 목록 (발송 없음, 읽기 전용).
// 팝업이 이 후보 전체를 받아 체크 선택하므로 dealIds 없이 호출 = 현재 목록 전체 후보.
// dealIds(선택): 키워드 검색 중일 때 화면에 보이는 딜 id — 후보를 검색 결과로 한정.
// 빈 배열은 "전체" 로 오해되지 않도록 requireDealIds 로 거부한다.
// { groups: [{recipientEmail, recipientName, dealCount,
//             deals: [{id, projectName, customerCompany, daysSinceUpdate}]}],
//   totalDeals, skippedAlreadyNotified, skippedNoRecipient, skippedNotInList }
export async function previewBulkNotice(days, dealIds) {
  const params = new URLSearchParams();
  if (days) params.set("days", String(days));
  if (dealIds !== undefined) params.set("dealIds", requireDealIds(dealIds).join(","));
  const qs = params.toString();
  return api.get(`/admin/stale-deals/notice-bulk/preview${qs ? `?${qs}` : ""}`);
}

// 일괄 발송 — 수신자(등록 계정)별 1통에 딜 목록 정리. dealIds: 팝업에서 체크한 딜 id 배열(필수)
// { ok, sentMails, sentDeals, failedMails, failedMarks,
//   skippedAlreadyNotified, skippedNoRecipient, skippedNotInList }
// failedMarks: 메일 발송 후 이력 마킹 실패 건수(다음 일괄 발송에서 중복 발송 가능)
export async function sendBulkNotice(days, dealIds) {
  return api.post(`/admin/stale-deals/notice-bulk`, { days, dealIds: requireDealIds(dealIds) });
}

// 현재 목록 엑셀(.xlsx) 다운로드 — Authorization 헤더 필요해 fetch 후 Blob 저장.
// q 는 목록과 같은 키워드 — 화면에 보이는 딜만 내려받는다. (adminSignups.downloadAttachment 와 동일 패턴)
export async function downloadExcel(days, q) {
  const token = getAccessToken();
  const res = await fetch(`/api/admin/stale-deals/export${staleQuery(days, q)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    // api 레이어는 i18n 미사용 — 영문 폴백. 호출자가 i18n 폴백으로 덮어쓴다.
    let msg = "Download failed";
    try {
      const j = await res.json();
      msg = (j && j.error && j.error.message) || msg;
    } catch (e) {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  // 파일명은 서버 Content-Disposition 의 filename*(UTF-8) 우선 사용
  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename\*=UTF-8''([^;]+)/);
  const filename = m ? decodeURIComponent(m[1]) : `stale-deals_${days || "all"}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}
